/**
 * Event Types
 *
 * Type definitions for cell and keyboard events.
 */

/**
 * Key codes used in the puzzle builder
 */
export const KeyCode = {
    BACKSPACE: 8,
    TAB: 9,
    ENTER: 13,
    DELETE: 46,
    INSERT: 45,
    SPACE: 32,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    A: 65,
    E: 69,
    F: 70,
    H: 72,
    I: 73,
    M: 77,
    N: 78,
    S: 83,
    V: 86,
    Y: 89,
    Z: 90,
} as const;

export type KeyCodeValue = (typeof KeyCode)[keyof typeof KeyCode];

/**
 * Navigation direction for cell movement
 */
export type NavigationDirection = 'left' | 'right' | 'up' | 'down';

/**
 * Event handler context containing required dependencies
 */
export interface CellEventContext {
    /** Get current element */
    getCurrentElement: () => HTMLElement | null;
    /** Set current element */
    setCurrentElement: (element: HTMLElement | null) => void;
    /** Get current cell ID */
    getCurrentCellId: () => number;
    /** Set current cell ID */
    setCurrentCellId: (id: number) => void;
    /** Get current direction (horizontal or vertical) */
    getIsHorizontal: () => boolean;
    /** Set current direction */
    setIsHorizontal: (value: boolean) => void;
    /** Get builder mode state */
    getIsBuilderMode: () => boolean;
    /** Get cell highlighting state */
    getCellHighlight: () => boolean;
    /** Set cell highlighting state */
    setCellHighlight: (value: boolean) => void;
    /** Get clue marking state */
    getClueMarking: () => boolean;
    /** Get sync editing state */
    getSyncEditing: () => boolean;
    /** Get valid character pattern */
    getValidCharPattern: () => string;
    /** Get current selection array */
    getCurrentSelection: () => number[];
    /** Set current selection array */
    setCurrentSelection: (ids: number[]) => void;
    /** Get inverse selection array */
    getInverseSelection: () => number[];
    /** Set inverse selection array */
    setInverseSelection: (ids: number[]) => void;
    /** Mark state as unsaved */
    setUnsaved: () => void;
    /** Mark state as unverified */
    setUnverified: () => void;
}

/**
 * Cell action type for history tracking
 */
export type CellActionType =
    | 'character_input'
    | 'character_delete'
    | 'cell_clear'
    | 'toggle_black'
    | 'toggle_image'
    | 'toggle_empty'
    | 'toggle_colored'
    | 'toggle_bridge'
    | 'toggle_vertical_line'
    | 'toggle_horizontal_line'
    | 'toggle_known'
    | 'set_clue'
    | 'set_solution';

/**
 * Cell action data for history
 */
export interface CellAction {
    type: CellActionType;
    cellId: number;
    previousValue?: string | null;
    newValue?: string | null;
    description: string;
}
